/**
 * Testimonial slider: equalize slide heights and run after Swiper is ready.
 */

const TSLIDER_SECTION = '.e-stn--testimonial-slider';
const TSLIDER_SWIPER = '.e-sld .swiper';
const TSLIDER_SLIDE = '.swiper-slide';
const TSLIDER_MAX_ATTEMPTS = 30;
const TSLIDER_POLL_MS = 50;

const tsliderIsAdmin = () => document.body.classList.contains( 'wp-admin' );

/**
 * Equalize heights of all slides in a section so they match the tallest slide.
 *
 * @param {Element} section - .e-stn--testimonial-slider section
 */
function tsliderEqualizeSlideHeights( section ) {
  const swiperEl = section.querySelector( TSLIDER_SWIPER );
  if ( ! swiperEl ) return;

  const slides = swiperEl.querySelectorAll( TSLIDER_SLIDE );
  if ( ! slides.length ) return;

  slides.forEach( ( el ) => {
    el.style.minHeight = '';
  } );

  let maxH = 0;
  slides.forEach( ( el ) => {
    if ( el.offsetHeight > maxH ) maxH = el.offsetHeight;
  } );

  slides.forEach( ( el ) => {
    el.style.minHeight = `${ maxH }px`;
  } );
}

/**
 * Run equalize for all testimonial slider sections.
 */
function tsliderEqualizeAll() {
  document.querySelectorAll( TSLIDER_SECTION ).forEach( tsliderEqualizeSlideHeights );
}

/**
 * Move slider prev/next controllers into the first column div
 *
 * @param {Element} section - .e-stn--testimonial-slider section
 */
function tsliderMoveControllersToFirstColumn( section ) {
  const firstColDiv = section.querySelector( '.e-col:first-child > .e-div' );
  const controllers = section.querySelector( '.e-sld__controllers' );
  if ( ! firstColDiv || ! controllers || firstColDiv.contains( controllers ) ) return;
  firstColDiv.appendChild( controllers );
}

/**
 * Pause all videos in a section.
 *
 * @param {Element} section - .e-stn--testimonial-slider section
 */
function tsliderPauseAllVideosInSection( section ) {
  section.querySelectorAll( 'video' ).forEach( ( video ) => {
    video.pause();
  } );
}

/**
 * Play video in the active slide, pause in all others.
 *
 * @param {Element} section - .e-stn--testimonial-slider section
 */
function tsliderSyncVideoToActiveSlide( section ) {
  const swiperEl = section.querySelector( TSLIDER_SWIPER );
  if ( ! swiperEl?.swiper ) return;

  const swiper = swiperEl.swiper;
  const slides = swiper.slides || swiperEl.querySelectorAll( TSLIDER_SLIDE );
  const activeIndex = swiper.activeIndex;
  const activeSlide = slides[ activeIndex ];
  if ( ! activeSlide ) return;

  tsliderPauseAllVideosInSection( section );
  const activeVideo = activeSlide.querySelector( 'video' );
  if ( activeVideo ) {
    const playPromise = activeVideo.play();
    if ( playPromise && typeof playPromise.catch === 'function' ) {
      playPromise.catch( () => {} );
    }
  }
}

/**
 * Attach slide-change and init video play/pause for one section.
 *
 * @param {Element} section - .e-stn--testimonial-slider section
 */
function tsliderSetupVideoPlayback( section ) {
  const swiperEl = section.querySelector( TSLIDER_SWIPER );
  if ( ! swiperEl ) return;

  const swiper = swiperEl.swiper;
  if ( ! swiper ) return;

  tsliderSyncVideoToActiveSlide( section );
  swiper.on( 'slideChangeTransitionEnd', () => {
    tsliderSyncVideoToActiveSlide( section );
  } );
}

/**
 * Setup video play/pause for all testimonial sections (when Swiper is ready).
 */
function tsliderSetupAllVideoPlayback() {
  document.querySelectorAll( TSLIDER_SECTION ).forEach( ( section ) => {
    if ( section.querySelector( TSLIDER_SWIPER )?.swiper ) {
      tsliderSetupVideoPlayback( section );
    }
  } );
}

/**
 * Check if at least one section has its Swiper initialized (has width/slides).
 */
function tsliderIsSwiperReady( sections ) {
  for ( const section of sections ) {
    const swiperEl = section.querySelector( TSLIDER_SWIPER );
    if ( ! swiperEl ) continue;
    const slides = swiperEl.querySelectorAll( TSLIDER_SLIDE );
    if ( slides.length && slides[ 0 ].offsetWidth > 0 ) return true;
  }
  return false;
}

/**
 * Check if Swiper instance is attached to the element (theme may attach after layout).
 */
function tsliderHasSwiperInstance( sections ) {
  for ( const section of sections ) {
    if ( section.querySelector( TSLIDER_SWIPER )?.swiper ) return true;
  }
  return false;
}

/**
 * Poll until Swiper is initialized then equalize heights and setup video playback.
 */
function tsliderInitWhenReady() {
  if ( tsliderIsAdmin() ) return;

  let attempts = 0;
  const tryInit = () => {
    const sections = document.querySelectorAll( TSLIDER_SECTION );
    if ( ! sections.length ) return;

    if ( ! tsliderIsSwiperReady( sections ) && attempts < TSLIDER_MAX_ATTEMPTS ) {
      attempts++;
      setTimeout( tryInit, TSLIDER_POLL_MS );
      return;
    }

    tsliderEqualizeAll();
    sections.forEach( tsliderMoveControllersToFirstColumn );

    if ( tsliderHasSwiperInstance( sections ) ) {
      tsliderSetupAllVideoPlayback();
    } else {
      setTimeout( () => tsliderSetupAllVideoPlayback(), 150 );
    }
  };

  setTimeout( tryInit, TSLIDER_POLL_MS );
}

/**
 * Debounced resize: re-equalize heights.
 */
function tsliderAddResizeListener() {
  let timer;
  window.addEventListener( 'resize', () => {
    clearTimeout( timer );
    timer = setTimeout( tsliderEqualizeAll, 200 );
  } );
}

function tsliderBoot() {
  if ( tsliderIsAdmin() ) return;

  const state = document.readyState;
  const needsLoad = state === 'loading' || state === 'interactive';

  if ( needsLoad ) {
    window.addEventListener( 'load', tsliderInitWhenReady );
  } else {
    tsliderInitWhenReady();
  }

  tsliderAddResizeListener();
}

tsliderBoot();
